import { supabase } from '@/lib/supabase'
import type {
  GuildSkill,
  GuildProject,
  ProfileWithSkills,
  ProjectWithOwner,
  RoleWithProject,
  ApplicationWithRole,
  ApplicationWithApplicant,
  ApplicationStatus,
  RoleStatus,
  PortfolioItemWithWork,
  WorkSummary,
  ConversationSummary,
  GuildMessage,
  GuildNotification,
  StudioInvite,
  CreatorPayoutAccount,
  GuildReview,
  ReviewWithProject,
} from '@/types/guild'
import type { TablesInsert, TablesUpdate } from '@/types/database'

const PROFILE_WITH_SKILLS = '*, guild_profile_skills(skill_id, guild_skills(*))'
const ROLE_WITH_PROJECT =
  '*, guild_skills(*), guild_projects(*, guild_profiles(user_id, display_name, avatar_url))'

export async function fetchSkills(): Promise<GuildSkill[]> {
  const { data, error } = await supabase
    .from('guild_skills')
    .select('*')
    .order('category')
    .order('name')
  if (error) throw error
  return data
}

export async function fetchMembers(): Promise<ProfileWithSkills[]> {
  const { data, error } = await supabase
    .from('guild_profiles')
    .select(PROFILE_WITH_SKILLS)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as ProfileWithSkills[]
}

export async function fetchMember(userId: string): Promise<ProfileWithSkills | null> {
  const { data, error } = await supabase
    .from('guild_profiles')
    .select(PROFILE_WITH_SKILLS)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data as ProfileWithSkills | null
}

export async function upsertProfile(profile: TablesInsert<'guild_profiles'>) {
  const { error } = await supabase.from('guild_profiles').upsert(profile)
  if (error) throw error
}

export async function setProfileSkills(profileId: string, skillIds: number[]) {
  const { error: deleteError } = await supabase
    .from('guild_profile_skills')
    .delete()
    .eq('profile_id', profileId)
  if (deleteError) throw deleteError

  if (skillIds.length > 0) {
    const { error } = await supabase
      .from('guild_profile_skills')
      .insert(skillIds.map(skill_id => ({ profile_id: profileId, skill_id })))
    if (error) throw error
  }
}

export async function fetchProjects(): Promise<ProjectWithOwner[]> {
  const { data, error } = await supabase
    .from('guild_projects')
    .select('*, guild_profiles(user_id, display_name, avatar_url)')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as ProjectWithOwner[]
}

export async function fetchProject(id: string): Promise<ProjectWithOwner | null> {
  const { data, error } = await supabase
    .from('guild_projects')
    .select('*, guild_profiles(user_id, display_name, avatar_url)')
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data as ProjectWithOwner | null
}

export async function createProject(
  project: TablesInsert<'guild_projects'>
): Promise<GuildProject> {
  const { data, error } = await supabase
    .from('guild_projects')
    .insert(project)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateProject(id: string, updates: TablesUpdate<'guild_projects'>) {
  const { error } = await supabase.from('guild_projects').update(updates).eq('id', id)
  if (error) throw error
}

export async function fetchProjectRoles(projectId: string): Promise<RoleWithProject[]> {
  const { data, error } = await supabase
    .from('guild_project_roles')
    .select(ROLE_WITH_PROJECT)
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as RoleWithProject[]
}

export async function fetchOpenRoles(): Promise<RoleWithProject[]> {
  const { data, error } = await supabase
    .from('guild_project_roles')
    .select(ROLE_WITH_PROJECT)
    .eq('status', 'open')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as RoleWithProject[]
}

export async function createRole(role: TablesInsert<'guild_project_roles'>) {
  const { error } = await supabase.from('guild_project_roles').insert(role)
  if (error) throw error
}

export async function updateRoleStatus(roleId: string, status: RoleStatus) {
  const { error } = await supabase
    .from('guild_project_roles')
    .update({ status })
    .eq('id', roleId)
  if (error) throw error
}

export async function applyToRole(roleId: string, applicantId: string, message: string) {
  const { error } = await supabase.from('guild_role_applications').insert({
    role_id: roleId,
    applicant_id: applicantId,
    message: message || null,
  })
  if (error) throw error
}

export async function fetchMyApplications(userId: string): Promise<ApplicationWithRole[]> {
  const { data, error } = await supabase
    .from('guild_role_applications')
    .select(
      '*, guild_project_roles(*, guild_projects(*, guild_profiles(user_id, display_name, avatar_url))), guild_deal_memos(*)'
    )
    .eq('applicant_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as unknown as ApplicationWithRole[]
}

export type ReceivedApplication = ApplicationWithApplicant & {
  guild_project_roles: RoleWithProject
}

export async function fetchReceivedApplications(
  ownerId: string
): Promise<ReceivedApplication[]> {
  const { data, error } = await supabase
    .from('guild_role_applications')
    .select(
      '*, guild_profiles(user_id, display_name, avatar_url, headline), guild_project_roles!inner(*, guild_skills(*), guild_projects!inner(*, guild_profiles(user_id, display_name, avatar_url))), guild_deal_memos(*)'
    )
    .eq('guild_project_roles.guild_projects.owner_id', ownerId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as unknown as ReceivedApplication[]
}

export type ProjectApplication = ApplicationWithApplicant & {
  guild_project_roles: { id: string; project_id: string; title: string }
}

export async function fetchProjectApplications(
  projectId: string
): Promise<ProjectApplication[]> {
  const { data, error } = await supabase
    .from('guild_role_applications')
    .select(
      '*, guild_profiles(user_id, display_name, avatar_url, headline), guild_project_roles!inner(id, project_id, title), guild_deal_memos(*)'
    )
    .eq('guild_project_roles.project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as unknown as ProjectApplication[]
}

export async function updateApplicationStatus(id: string, status: ApplicationStatus) {
  const { error } = await supabase
    .from('guild_role_applications')
    .update({ status })
    .eq('id', id)
  if (error) throw error
}

const WORK_SUMMARY = 'id, title, type, poster_url, release_date'

export async function fetchPortfolio(profileId: string): Promise<PortfolioItemWithWork[]> {
  const { data, error } = await supabase
    .from('guild_portfolio_items')
    .select(`*, works(${WORK_SUMMARY})`)
    .eq('profile_id', profileId)
    .order('sort_order')
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as PortfolioItemWithWork[]
}

export async function createPortfolioItem(item: TablesInsert<'guild_portfolio_items'>) {
  const { error } = await supabase.from('guild_portfolio_items').insert(item)
  if (error) throw error
}

export async function updatePortfolioItem(
  id: string,
  updates: TablesUpdate<'guild_portfolio_items'>
) {
  const { error } = await supabase
    .from('guild_portfolio_items')
    .update(updates)
    .eq('id', id)
  if (error) throw error
}

export async function deletePortfolioItem(id: string) {
  const { error } = await supabase.from('guild_portfolio_items').delete().eq('id', id)
  if (error) throw error
}

export async function searchWorks(query: string): Promise<WorkSummary[]> {
  const { data, error } = await supabase
    .from('works')
    .select(WORK_SUMMARY)
    .ilike('title', `%${query}%`)
    .order('title')
    .limit(8)
  if (error) throw error
  return data as WorkSummary[]
}

export async function fetchMyApplicationRoleIds(userId: string): Promise<Set<string>> {
  const { data, error } = await supabase
    .from('guild_role_applications')
    .select('role_id')
    .eq('applicant_id', userId)
  if (error) throw error
  return new Set(data.map(row => row.role_id))
}

// ============ Messaging ============

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const { data, error } = await supabase
    .from('guild_conversations')
    .select(
      '*, guild_conversation_participants(profile_id, last_read_at, guild_profiles(user_id, display_name, avatar_url)), guild_messages(body, sender_id, created_at)'
    )
    .order('last_message_at', { ascending: false })
    .order('created_at', { referencedTable: 'guild_messages', ascending: false })
    .limit(1, { referencedTable: 'guild_messages' })
  if (error) throw error
  return data as unknown as ConversationSummary[]
}

export async function fetchMessages(conversationId: string): Promise<GuildMessage[]> {
  const { data, error } = await supabase
    .from('guild_messages')
    .select('*')
    .eq('conversation_id', conversationId)
    .order('created_at')
    .limit(500)
  if (error) throw error
  return data
}

export async function sendMessage(conversationId: string, senderId: string, body: string) {
  const { error } = await supabase.from('guild_messages').insert({
    conversation_id: conversationId,
    sender_id: senderId,
    body,
  })
  if (error) throw error
}

export async function markConversationRead(conversationId: string, profileId: string) {
  const { error } = await supabase
    .from('guild_conversation_participants')
    .update({ last_read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('profile_id', profileId)
  if (error) throw error
}

export async function startDm(otherProfileId: string): Promise<string> {
  const { data, error } = await supabase.rpc('start_dm', {
    other_profile_id: otherProfileId,
  })
  if (error) throw error
  return data as string
}

export async function fetchUnreadConversationCount(profileId: string): Promise<number> {
  const { data, error } = await supabase
    .from('guild_conversation_participants')
    .select('conversation_id, last_read_at, guild_conversations!inner(last_message_at)')
    .eq('profile_id', profileId)
  if (error) throw error
  return data.filter(
    row =>
      new Date(row.guild_conversations.last_message_at).getTime() >
      new Date(row.last_read_at).getTime()
  ).length
}

// ============ Notifications (shared cross-app table, guild scope) ============

export async function fetchNotifications(): Promise<GuildNotification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('app', 'guild')
    .order('created_at', { ascending: false })
    .limit(50)
  if (error) throw error
  return data
}

export async function fetchUnreadNotificationCount(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('app', 'guild')
    .is('read_at', null)
  if (error) throw error
  return count ?? 0
}

export async function markNotificationRead(id: string) {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
}

export async function markAllNotificationsRead() {
  const { error } = await supabase
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('app', 'guild')
    .is('read_at', null)
  if (error) throw error
}

// ============ Studio bridge ============

export async function foundStudio(
  name: string,
  description: string | null,
  fromProjectId: string | null
): Promise<string> {
  const { data, error } = await supabase.rpc('found_studio', {
    studio_name: name,
    studio_description: description ?? undefined,
    from_project_id: fromProjectId ?? undefined,
  })
  if (error) throw error
  return data as string
}

export async function fetchMyInvites(): Promise<StudioInvite[]> {
  const { data, error } = await supabase.rpc('my_studio_invites')
  if (error) throw error
  return data as StudioInvite[]
}

export async function respondToStudioInvite(inviteId: string, accept: boolean) {
  const { error } = await supabase.rpc('respond_to_studio_invite', {
    invite_id: inviteId,
    accept,
  })
  if (error) throw error
}

export type StudioSummary = { id: string; company_name: string; brand_slug: string | null }

export async function fetchStudio(publisherId: string): Promise<StudioSummary | null> {
  const { data, error } = await supabase
    .from('publishers')
    .select('id, company_name, brand_slug')
    .eq('id', publisherId)
    .maybeSingle()
  if (error) throw error
  return data
}

export async function fetchStudioWorks(publisherId: string): Promise<WorkSummary[]> {
  const { data, error } = await supabase
    .from('works')
    .select(WORK_SUMMARY)
    .eq('publisher_id', publisherId)
    .order('created_at', { ascending: false })
    .limit(25)
  if (error) throw error
  return data as WorkSummary[]
}

export async function linkWorkToProject(projectId: string, workId: string | null) {
  const { error } = await supabase
    .from('guild_projects')
    .update({ work_id: workId })
    .eq('id', projectId)
  if (error) throw error
}

export async function syncWorkCredits(projectId: string): Promise<number> {
  const { data, error } = await supabase.rpc('sync_work_credits', {
    p_project_id: projectId,
  })
  if (error) throw error
  return data as number
}

// ============ Deal memos ============

export async function createDealMemo(memo: TablesInsert<'guild_deal_memos'>) {
  const { error } = await supabase.from('guild_deal_memos').insert(memo)
  if (error) throw error
}

type DealMemoTerms = Pick<
  TablesInsert<'guild_deal_memos'>,
  'compensation' | 'rate_detail' | 'revenue_share_percent' | 'terms'
>

export async function resendDealMemo(id: string, terms: DealMemoTerms) {
  const { error } = await supabase
    .from('guild_deal_memos')
    .update({ ...terms, status: 'proposed' })
    .eq('id', id)
  if (error) throw error
}

export async function cancelDealMemo(id: string) {
  const { error } = await supabase
    .from('guild_deal_memos')
    .update({ status: 'cancelled' })
    .eq('id', id)
  if (error) throw error
}

export async function respondToDealMemo(id: string, accept: boolean) {
  const { error } = await supabase
    .from('guild_deal_memos')
    .update({ status: accept ? 'accepted' : 'declined' })
    .eq('id', id)
  if (error) throw error
}

export async function applyRevenueSplits(projectId: string): Promise<number> {
  const { data, error } = await supabase.rpc('apply_deal_memos_to_revenue_contract', {
    p_project_id: projectId,
  })
  if (error) throw error
  return data as number
}

// ============ Creator payouts (Stripe Connect) ============

export async function fetchCreatorPayoutAccount(
  userId: string
): Promise<CreatorPayoutAccount | null> {
  const { data, error } = await supabase
    .from('creator_payout_accounts')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()
  if (error) throw error
  return data
}

// ============ Reviews (P2-1) ============

export async function projectReviewsOpen(projectId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('project_reviews_open', {
    p_project_id: projectId,
  })
  if (error) throw error
  return Boolean(data)
}

export async function fetchProjectReviews(projectId: string): Promise<GuildReview[]> {
  const { data, error } = await supabase
    .from('guild_reviews')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data ?? []
}

export async function fetchReviewsForMember(userId: string): Promise<ReviewWithProject[]> {
  const { data, error } = await supabase
    .from('guild_reviews')
    .select(
      '*, guild_projects(id, title), guild_profiles!guild_reviews_reviewer_id_fkey(user_id, display_name, avatar_url)'
    )
    .eq('subject_id', userId)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data as unknown as ReviewWithProject[]
}

export async function fetchMyAcceptedApplicationOnProject(
  projectId: string,
  userId: string
): Promise<ProjectApplication | null> {
  const { data, error } = await supabase
    .from('guild_role_applications')
    .select(
      '*, guild_profiles(user_id, display_name, avatar_url, headline), guild_project_roles!inner(id, project_id, title), guild_deal_memos(*)'
    )
    .eq('applicant_id', userId)
    .eq('status', 'accepted')
    .eq('guild_project_roles.project_id', projectId)
    .maybeSingle()
  if (error) throw error
  return (data as unknown as ProjectApplication | null) ?? null
}

export async function submitGuildReview(input: {
  projectId: string
  subjectId: string
  rating: number
  body?: string | null
  roleTitle?: string | null
}) {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser()
  if (authError || !user) throw new Error('Not signed in')

  const { error } = await supabase.from('guild_reviews').insert({
    project_id: input.projectId,
    subject_id: input.subjectId,
    reviewer_id: user.id,
    rating: input.rating,
    body: input.body ?? null,
    role_title: input.roleTitle ?? null,
  })
  if (error) throw error
}
